import { z } from 'zod';

import {
    schemaEuropacePrivateKrankenversicherungUndUnterhaltsverpflichtung,
    schemaEuropaceSonstigeAusgabeUndMietAusgabe,
} from './Ausgaben';
import {
    schemaEuropaceEhegattenunterhaltSonstigeEinnahmeEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente,
    schemaEuropaceEinkunftAusNebentaetigkeit,
} from './Einnahmen';
import { schemaEuropaceImmobilie } from './Immobilie.schema';
import { schemaEuropaceKind } from './Kind.schema';
import { schemaEuropaceKontoverbindung } from './Kontoverbindung.schema';
import {
    schemaEuropaceDispositionskredit,
    schemaEuropaceKreditkarte,
    schemaEuropaceLeasing,
    schemaEuropaceRatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit,
} from './Verbindlichkeiten';
import { schemaEuropaceBausparvertrag, schemaEuropaceLebensversicherung } from './Vermoegen';

/*
	https://docs.api.europace.de/privatkredit/vorgaenge/kex-vorgang-import-api/#haushalt

	{
        "ausgaben": {
            "mietausgaben": [ Mietausgabe ],
            "privateKrankenversicherungen": [ Private Krankenversicherung ],
            "unterhaltsverpflichtungen": [ Unterhaltsverpflichtung ],
            "sonstigeAusgaben": [ Sonstige Ausgabe ]
        },
         "einnahmen": {
            "ehegattenunterhalt": [ Ehegattenunterhalt ],
            "einkuenfteAusNebentaetigkeit": [ Einkunft aus Nebentätigkeit ],
            "sonstigeEinnahmen": [ Sonstige Einnahme ],
            "unbefristeteZusatzrenten": [ Unbefristete Zusatzrente ]
        },
        "verbindlichkeiten": {
            "dispositionskredite": [ Dispositionskredit ],
            "kreditkarten": [ Kreditkarte ],
            "leasings": [ Leasing ],
            "ratenkredite": [ Ratenkredit ],
            "sonstigeVerbindlichkeiten": [ Sonstige Verbindlichkeit ]
        },
        "vermoegen": {
            "bausparvertraege": [ Bausparvertrag ],
            "lebensversicherungen": [ Lebensversicherung ]
        },
        "immobilien": [ Immobilie ],
        "kinder": [ kind ],
        "kontoverbindung": {
            "bic": String,
            "gehoertZuAntragsteller": Antragstellerzuordnung,
            "iban": String,
            "kreditinstitut": String
        }
    }


*/

export const schemaEuropaceHaushalt = z.object({
    ausgaben: z.optional(
        z.object({
            mietausgaben: z.optional(z.array(schemaEuropaceSonstigeAusgabeUndMietAusgabe)),
            privateKrankenversicherungen: z.optional(
                z.array(schemaEuropacePrivateKrankenversicherungUndUnterhaltsverpflichtung),
            ),
            sonstigeAusgaben: z.optional(z.array(schemaEuropaceSonstigeAusgabeUndMietAusgabe)),
            unterhaltsverpflichtungen: z.optional(
                z.array(schemaEuropacePrivateKrankenversicherungUndUnterhaltsverpflichtung),
            ),
        }),
    ),
    einnahmen: z.optional(
        z.object({
            ehegattenunterhalt: z.optional(
                z.array(
                    schemaEuropaceEhegattenunterhaltSonstigeEinnahmeEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente,
                ),
            ),
            einkuenfteAusNebentaetigkeit: z.optional(z.array(schemaEuropaceEinkunftAusNebentaetigkeit)),
            sonstigeEinnahmen: z.optional(
                z.array(
                    schemaEuropaceEhegattenunterhaltSonstigeEinnahmeEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente,
                ),
            ),
            unbefristeteZusatzrenten: z.optional(
                z.array(
                    schemaEuropaceEhegattenunterhaltSonstigeEinnahmeEinkunftAusKapitalvermoegenUndUnbefristeteZusatzrente,
                ),
            ),
        }),
    ),
    immobilien: z.optional(z.array(schemaEuropaceImmobilie)),
    kinder: z.optional(z.array(schemaEuropaceKind)),
    kontoverbindung: z.optional(schemaEuropaceKontoverbindung),
    verbindlichkeiten: z.optional(
        z.object({
            dispositionskredite: z.optional(z.array(schemaEuropaceDispositionskredit)),
            kreditkarten: z.optional(z.array(schemaEuropaceKreditkarte)),
            leasings: z.optional(z.array(schemaEuropaceLeasing)),
            ratenkredite: z.optional(z.array(schemaEuropaceRatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit)),
            sonstigeVerbindlichkeiten: z.optional(
                z.array(schemaEuropaceRatenkreditGeschaeftskreditUndSonstigeVerbindlichkeit),
            ),
        }),
    ),
    vermoegen: z.optional(
        z.object({
            bausparvertraege: z.optional(z.array(schemaEuropaceBausparvertrag)),
            lebensversicherungen: z.optional(z.array(schemaEuropaceLebensversicherung)),
        }),
    ),
});

export type SchemaEuropaceHaushalt = z.infer<typeof schemaEuropaceHaushalt>;
